package com.neurobs.ccab

import com.neurobs.util.{Guid, JsonPrinter, SecondsTimestamp, groupConsecutive, sequence}
import io.circe.generic.auto._

import scala.collection.mutable.ArrayBuffer


class StudySessionManager(val config: SessionConfig) {
	import StudySessionManager._
	
	def getSessionSuggestion(now: SecondsTimestamp, studyId: Long, studyConfig: SessionConfig.StudyConfig, tsSessions: Seq[db.SessionData]): Option[SubjectSessionHistory] = {
		for {
			protocol <- config.protocols.find(_.id == studyConfig.protocol)
			previous <- sequence(tsSessions.sortWith(_.startTime < _.startTime).map(TSSession.apply))
		} yield {
			val restrictions = convert(intersect(protocol.parameter_restrictions.getOrElse(Seq()), studyConfig.parameter_restrictions.getOrElse(Seq())), config)

			val startTime = if (previous.isEmpty) SecondsTimestamp.now() else previous.head.startTime
			val combined = CombinedTSSession.combine(studyId, previous)
			val studySessions = StudySessionList(startTime, protocol.sessions)

			val logical = LogicalSession.compare(combined, studySessions)
			val lastPreviousIndex = logical.lastIndexWhere(_.combinedSession.isDefined)
			val history = logical.take(lastPreviousIndex + 1)
			def res(suggestion: Option[LogicalSession]): SubjectSessionHistory = SubjectSessionHistory(logicalSessions = history, suggestion = suggestion, protocol = protocol, combinedRestrictions = restrictions)

			if (lastPreviousIndex == -1) {
				// first session for this subject
				res(logical.headOption)
			}
			else {
				val last = logical(lastPreviousIndex)
				if (last.combinedSession.get.startTime.withinSeconds(now, COMBINE_TIME_INTERVAL_SECONDS)) {
					if (last.studySession.isDefined) {
						// same day, suggest same study session
						res(Some(last))
					}
					else {
						// same day, but don't know what they're doing
						res(None)
					}
				}
				else if (lastPreviousIndex == logical.length - 1) {
					// no study sessions after this
					res(None)
				}
				else {
					val nextStudySession = logical(lastPreviousIndex + 1)
					if (nextStudySession.studySession.get.intendedTime.withinSeconds(now, MATCHING_INTERVAL_SECONDS))
						res(Some(nextStudySession))
					else
						res(None)
				}
			}
		}
	}
}

object StudySessionManager {
	import SessionConfig._

	val MATCHING_INTERVAL_SECONDS = java.time.Duration.ofDays(90).getSeconds
	val COMBINE_TIME_INTERVAL_SECONDS = java.time.Duration.ofHours(12).getSeconds

	case class TSSession
	(
		data: db.SessionData,
		properties: TestSessionProperties
	) {
		def sameSequence(s: Session): Boolean = (properties.group == s.group_name) && (properties.sequence == s.sequence_name)
		def sameSequence(s: TSSession): Boolean = (properties.group == s.properties.group) && (properties.sequence == s.properties.sequence)
		def startTime: SecondsTimestamp = data.startTime
	}
	object TSSession {
		def apply(data: db.SessionData): Option[TSSession] = data.properties.flatMap(JsonPrinter.fromJson[TestSessionProperties](_).map(TSSession(data, _)))
	}

	case class CombinedTSSession
	(
		thisStudy: Boolean,
		startTime: SecondsTimestamp,
		tsSessions: Seq[TSSession]
	)
	object CombinedTSSession {
		def combine(studyId: Long, sessions: Seq[TSSession]): Seq[CombinedTSSession] = {
			val sorted = sessions.sortWith(_.startTime < _.startTime)
			val grouped = groupConsecutive(sorted)((lhs, rhs) =>
				(lhs.data.studyId == rhs.data.studyId) &&
				lhs.sameSequence(rhs) &&
				lhs.data.startTime.withinSeconds(rhs.startTime, COMBINE_TIME_INTERVAL_SECONDS)
			)
			grouped.map(i => CombinedTSSession(
				thisStudy = i.head.data.studyId == studyId,
				startTime = i.head.startTime,
				tsSessions = i
			))
		}
	}

	class StudySession(val studySession: Session, var intendedTime: SecondsTimestamp, var relativeTimeBase: Boolean) {
		override def toString: String = s"($studySession, $intendedTime, $relativeTimeBase)"
	}
	object StudySession {
		def apply(studySession: Session, intendedTime: SecondsTimestamp): StudySession = new StudySession(studySession, intendedTime, studySession.getRequired)
	}

	class StudySessionList(val startTime: SecondsTimestamp, val sessions: Seq[StudySession]) {
		def updateIntendedTimes(): Unit = {
			var lastSessionTime = startTime
			for (s <- sessions) {
				val timeOrigin = if (s.studySession.getTimeType == TimeType.Absolute) startTime else lastSessionTime
				s.intendedTime = addDuration(timeOrigin, s.studySession.getTime)
				if (s.relativeTimeBase) lastSessionTime = s.intendedTime
			}
		}
		override def toString: String = s"($startTime, $sessions)"
	}
	object StudySessionList {
		def apply(startTime: SecondsTimestamp, studySessions: Seq[Session]): StudySessionList = {
			val temp = studySessions.map(StudySession(_, startTime))
			val res = new StudySessionList(startTime, temp)
			res.updateIntendedTimes()
			res
		}
	}

	case class LogicalSession
	(
		combinedSession: Option[CombinedTSSession] = None,
		studySession: Option[StudySession] = None
	) {
		def delayDays: Option[Long] = {
			for (c <- combinedSession; s <- studySession) yield (c.startTime.instant.getEpochSecond - s.intendedTime.instant.getEpochSecond) / 86400
		}
		def shortDescription: String = {
			combinedSession match {
				case None =>
					studySession match {
						case None =>
							"INVALID LogicalSession"
						case Some(study) =>
							val label = if (study.studySession.getRequired) "MISSED" else "Skipped optional"
							s"$label ${study.studySession.display_name} (${study.studySession.label})"
					}
				case Some(combined) =>
					studySession match {
						case None =>
							if (!combined.thisStudy) s"DIFFERENT STUDY sequence ${combined.tsSessions.head.properties.sequence}"
							else s"UNMATCHED sequence ${combined.tsSessions.head.properties.sequence}"
						case Some(study) =>
							val daysOff = delayDays.getOrElse(0)
							s"${study.studySession.display_name} (${study.studySession.label})"
					}
			}
		}
		override def toString = {
			combinedSession match {
				case None =>
					studySession match {
						case None =>
							"INVALID LogicalSession"
						case Some(study) =>
							val label = if (study.studySession.getRequired) "MISSED" else "Skipped optional"
							s"$label study session ${study.studySession.display_name} (${study.studySession.label}) at ${study.intendedTime.toString}"
					}
				case Some(combined) =>
					studySession match {
						case None =>
							if (!combined.thisStudy) s"WRONG study sequence ${combined.tsSessions.head.properties.sequence} at ${combined.startTime}"
							else s"UNMATCHED study sequence ${combined.tsSessions.head.properties.sequence} at ${combined.startTime}"
						case Some(study) =>
							val daysOff = delayDays.getOrElse(0)
							s"Matched study session ${study.studySession.display_name} (${study.studySession.label}) at ${combined.startTime} intended time ${study.intendedTime.toString} ($daysOff days)"
					}
			}
		}
		def startTime: SecondsTimestamp = combinedSession match {
			case Some(s) => s.startTime
			case None => studySession match {
				case Some(s) => s.intendedTime
				case None => SecondsTimestamp.now() // should never happen
			}
		}
		def key: String = studySession match {
			case Some(s) => s.studySession.id.noBracketsString
			case None => combinedSession.map(_.startTime.toString).getOrElse("")
		}
		def resumableTSSessions(resumableGuids: Seq[Guid]): Seq[db.SessionData] = {
			combinedSession match {
				case None => Seq()
				case Some(cs) =>
					cs.tsSessions.filter(s => resumableGuids.contains(s.data.subjectGuid)).map(_.data)
			}
		}
	}
	object LogicalSession {
		def distanceSeconds(t1: SecondsTimestamp, t2: SecondsTimestamp): Long = {
			val diff = t2.instant.getEpochSecond() - t1.instant.getEpochSecond()
			if (diff < 0) -diff else diff
		}
		def compare(history: Seq[CombinedTSSession], protocol: StudySessionList): Seq[LogicalSession] = {
			val res = ArrayBuffer[LogicalSession]()

			val hiter = history.iterator.buffered
			val siter = protocol.sessions.iterator.buffered
			while (hiter.hasNext || siter.hasNext) {
				if (!hiter.hasNext) res.append(LogicalSession(studySession = Some(siter.next())))
				else if (!siter.hasNext) res.append(LogicalSession(combinedSession = Some(hiter.next())))
				else {
					val h = hiter.head
					val s = siter.head
					if (h.thisStudy && h.tsSessions.head.sameSequence(s.studySession)) {
						val matchStudy: Option[StudySession] =
							if (h.startTime.withinSeconds(s.intendedTime, MATCHING_INTERVAL_SECONDS)) Some(siter.next())
							else {
								val current = siter.next()
								if (siter.hasNext) {
									val next = siter.head
									if (h.tsSessions.head.sameSequence(next.studySession) && (distanceSeconds(h.startTime, next.intendedTime) < distanceSeconds(h.startTime, current.intendedTime))) {
										// next study session is a better match so skip this one
										res.append(LogicalSession(studySession = Some(current)))
										None
									}
									else Some(current)
								}
								else Some(current)
							}
						matchStudy foreach { s =>
							s.relativeTimeBase = true
							protocol.updateIntendedTimes()
							res.append(LogicalSession(combinedSession = Some(hiter.next()), studySession = Some(s)))
						}
					}
					else if (h.startTime < s.intendedTime) res.append(LogicalSession(combinedSession = Some(hiter.next())))
					else res.append(LogicalSession(studySession = Some(siter.next())))
				}
			}

			res
		}
	}

	case class DisplayNameParameterRestriction
	(
		displayName: String,
		restriction: ParameterRestriction
	)

	case class SubjectSessionHistory
	(
		logicalSessions: Seq[LogicalSession],
		suggestion: Option[LogicalSession],
		protocol: Protocol,
		combinedRestrictions: Seq[DisplayNameParameterRestriction]
	) {
		def previousParameterValues(group: String, displayName: String): Seq[String] = {
			val all = for {
				logical <- logicalSessions
				combined <- logical.combinedSession.map(Seq(_)).getOrElse(Seq())
				session <- combined.tsSessions.filter(_.properties.group == group)
				value <- session.properties.params.filter(_.name == displayName).map(_.value).distinct
			} yield value
			all.distinct
		}
		def suggestionIndex: Option[Int] = for {
			s <- suggestion
			ss <- s.studySession
			index = protocol.sessions.indexWhere(_.id == ss.studySession.id)
			i <- if (index == -1) None else Some(index)
		} yield i
	}

	def intersect(lhs: Seq[ParameterRestriction], rhs: Seq[ParameterRestriction]): Seq[ParameterRestriction] = {
		val rhsMap = rhs.map(r => r.identifier -> r).toMap
		val lhsIntersected = lhs.map { l =>
			rhsMap.get(l.identifier) match {
				case Some(r) => l.intersect(r)
				case None    => l
			}
		}
		val rhsOnly = rhs.filterNot(r => lhs.exists(_.identifier == r.identifier))
		lhsIntersected ++ rhsOnly
	}

	def convert(restrictions: Seq[ParameterRestriction], config: SessionConfig): Seq[DisplayNameParameterRestriction] = {
		restrictions map { r =>
			val name = config.parameters.find(_.identifier == r.identifier).map(_.display_name)
			DisplayNameParameterRestriction(name.getOrElse(""), r)
		}
	}
}